import { mapValues } from 'es-toolkit';
import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { createSelector } from 'reselect';

import { PropertyTextInput } from '@paragon/client-tools';
import { SharedComponentSet } from '@paragon/contract-library';
import { getComponentDesignQuantities, getFlattenedChildrenWithCalculatedQuantity } from '@paragon/layout-models';
import { enqueueError } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';
import { naturalSortByProperty } from '@paragon/utilities';

import { createSharedComponentSet } from '../../../store/actions/sharedComponentSetActions';
import { getComponentDesignGuidToMostRecentAnalysisSetGuidMap } from '../../../store/selectors/getComponentDesignGuidToMostRecentAnalysisSetGuidMap';
import { getCommittedComponentDesigns } from '../../../store/selectors/getComponentDesignsSelectors';
import { getProjectLayoutModel } from '../../../store/selectors/getProjectLayoutModel';
import { areWorkingChangesByComponentDesignGuidMap } from '../../../store/selectors/workingChangesSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DialogTitleWithBackButton } from './DialogTitleWithBackButton';

interface Props {
  readonly setSharedComponentSet: (sharedComponentSet: SharedComponentSet) => void;
  readonly onNavigationBackRequested: () => void;
}

const getComponentDesignNamesMap = createSelector(getCommittedComponentDesigns, (componentDesigns) =>
  Object.fromEntries(componentDesigns.map((design) => [design.guid, design.component.name])),
);

export function CreateSharedComponentSet({ setSharedComponentSet, onNavigationBackRequested }: Props) {
  const dispatch = useAppDispatch();
  const openContext = useAppSelector((state) => state.application.openContext);
  const projectLayoutModel = useAppSelector(getProjectLayoutModel);
  const getComponentDesignQuantitiesWithModels = useAppSelector(getFlattenedChildrenWithCalculatedQuantity);
  const componentDesignQuantitiesFromProject = projectLayoutModel
    ? getComponentDesignQuantities(getComponentDesignQuantitiesWithModels(projectLayoutModel.guid))
    : null;
  const componentDesignNames = useAppSelector(getComponentDesignNamesMap);
  const workingChangesByComponentDesignGuidMap = useAppSelector(areWorkingChangesByComponentDesignGuidMap);
  const componentDesignGuidToMostRecentAnalysisSetGuidMap = useAppSelector(
    getComponentDesignGuidToMostRecentAnalysisSetGuidMap,
  );
  const componentDesignToAnalysisSetGuid = componentDesignQuantitiesFromProject
    ? mapValues(componentDesignQuantitiesFromProject, (_, componentDesignGuid) => {
        if (workingChangesByComponentDesignGuidMap[componentDesignGuid]) return null;
        return componentDesignGuidToMostRecentAnalysisSetGuidMap[componentDesignGuid];
      })
    : null;
  const carriedComponentConnectionsState = useAppSelector((state) => state.jackGirderConnections.data);

  function getComponentDesignGuidIsEligibleMap() {
    return mapValues(workingChangesByComponentDesignGuidMap, (areWorkingChanges) => !areWorkingChanges);
  }
  const ineligibleComponentCount = Object.values(workingChangesByComponentDesignGuidMap).filter(
    (areWorkingChanges) => areWorkingChanges,
  ).length;
  const [checkedComponentDesignState, setCheckedComponentDesignState] = useState<{
    [componentDesignGuid: string]: boolean;
  }>(getComponentDesignGuidIsEligibleMap());
  const [newSharedComponentSetName, setNewSharedComponentSetName] = useState<string>(
    projectLayoutModel?.name ?? 'Shared Trusses',
  );
  const [shareProjectSnapshot, setShareProjectSnapshot] = useState<boolean>(true);

  const checkedComponentDesignGuids = useMemo(
    () =>
      Object.entries(checkedComponentDesignState)
        .filter(([, checked]) => checked)
        .map(([componentDesignGuid]) => componentDesignGuid),
    [checkedComponentDesignState],
  );
  const uncheckedCarriedComponents = useMemo(() => {
    const carriedComponentConnections = Object.values(carriedComponentConnectionsState);
    const uncheckedCarryingComponents = new Set<string>();

    for (const connection of carriedComponentConnections) {
      if (
        !checkedComponentDesignGuids.includes(connection.girderGuid) &&
        !checkedComponentDesignGuids.includes(connection.jackGuid)
      )
        continue;

      const girderChecked = checkedComponentDesignGuids.includes(connection.girderGuid);
      const jackChecked = checkedComponentDesignGuids.includes(connection.jackGuid);

      if (!jackChecked && girderChecked) uncheckedCarryingComponents.add(connection.jackGuid);
    }

    return naturalSortByProperty(Array.from(uncheckedCarryingComponents), (guid) => componentDesignNames[guid]);
  }, [carriedComponentConnectionsState, checkedComponentDesignGuids, componentDesignNames]);

  if (openContext?.type !== 'project' || !componentDesignQuantitiesFromProject || !componentDesignToAnalysisSetGuid)
    return null;

  return (
    <Dialog
      insistent
      variant="design"
      hasTransition={false}
      title={<DialogTitleWithBackButton title="Create Shared Component Set" onBackClick={onNavigationBackRequested} />}
      onCancel={() => dispatch(closeCurrentPopup())}
      submitText="Create Shared Component Set"
      submitDisabled={
        !Object.values(checkedComponentDesignState).some((checked) => checked) || uncheckedCarriedComponents.length > 0
      }
      onSubmit={async () => {
        const componentDesignGuidAndQuantityEntries = Object.entries(componentDesignQuantitiesFromProject).filter(
          ([componentDesignGuid]) => checkedComponentDesignState[componentDesignGuid],
        );
        if (
          componentDesignGuidAndQuantityEntries.some(
            ([componentDesignGuid]) => !componentDesignToAnalysisSetGuid[componentDesignGuid],
          )
        ) {
          dispatch(enqueueError('Error creating Shared Component Set. Contact help@paragontruss.com.'));
          return;
        }
        // Sorting here facilitates the indices in the URL of QR codes for SharedComponentSets rising sequentially,
        // just as merged PDFs are sorted by component name.
        const analysisSetGuidsWithQuantities = naturalSortByProperty(
          componentDesignGuidAndQuantityEntries,
          ([componentDesignGuid]) => componentDesignNames[componentDesignGuid],
        ).map(([componentDesignGuid, quantity]) => ({
          analysisSetGuid: componentDesignToAnalysisSetGuid[componentDesignGuid]!,
          quantity,
        }));

        const newSharedComponentSet = await dispatch(
          createSharedComponentSet(
            newSharedComponentSetName,
            analysisSetGuidsWithQuantities,
            checkedComponentDesignGuids,
            shareProjectSnapshot,
          ),
        );
        if (newSharedComponentSet) setSharedComponentSet(newSharedComponentSet);
      }}
      contentSx={[{ display: 'flex', flexDirection: 'column', gap: 2 }, createScrollbarStyling]}
    >
      <Typography>
        Choose components to share (must be analyzed
        {ineligibleComponentCount > 0
          ? `, ${ineligibleComponentCount} ${ineligibleComponentCount === 1 ? 'is' : 'are'} not`
          : ''}
        )
      </Typography>
      <Box>
        <Button
          variant={'outlined'}
          sx={{ mr: 1 }}
          onClick={() => void setCheckedComponentDesignState(getComponentDesignGuidIsEligibleMap())}
        >
          Select All
        </Button>
        <Button
          variant={'outlined'}
          onClick={() => void setCheckedComponentDesignState((oldState) => mapValues(oldState, () => false))}
        >
          Clear All
        </Button>
      </Box>
      <Stack sx={[{ maxHeight: '250px', overflow: 'auto' }, createScrollbarStyling]}>
        {naturalSortByProperty(
          Object.entries(componentDesignQuantitiesFromProject),
          ([componentDesignGuid]) => componentDesignNames[componentDesignGuid],
        ).map(([componentDesignGuid, quantity]) => (
          <FormControlLabel
            key={componentDesignGuid}
            control={
              <Checkbox
                checked={checkedComponentDesignState[componentDesignGuid]}
                onChange={(_, checked) =>
                  setCheckedComponentDesignState((oldState) => ({
                    ...oldState,
                    [componentDesignGuid]: checked,
                  }))
                }
                disabled={workingChangesByComponentDesignGuidMap[componentDesignGuid]}
              />
            }
            label={`${componentDesignNames[componentDesignGuid]} (${quantity})`}
          />
        ))}
      </Stack>
      {uncheckedCarriedComponents.length > 0 && (
        <Box sx={{ width: 0, minWidth: '100%' }}>
          <Alert variant="outlined" severity="error">
            When carrying trusses are checked, their carried trusses must also be checked. Include:{' '}
            {uncheckedCarriedComponents
              .map((componentDesignGuid) => componentDesignNames[componentDesignGuid])
              .join(', ')}
          </Alert>
        </Box>
      )}
      <PropertyTextInput
        property="Shared Component Set Name"
        value={newSharedComponentSetName}
        submit={setNewSharedComponentSetName}
        error={newSharedComponentSetName.length === 0}
      />
      <FormControlLabel
        label="Share Project Snapshot (Hierarchy, Layout)"
        control={
          <Checkbox checked={shareProjectSnapshot} onChange={(_, checked) => void setShareProjectSnapshot(checked)} />
        }
      />
    </Dialog>
  );
}
